import {Pressable, View} from 'react-native';
import React, {FC, ReactNode, useEffect, useMemo} from 'react';
import {
  IPaywallFragmentFragment,
  usePaywalByIdQuery,
} from '~Api/Graphql/gql/types.generated';
import {mapHeroVideoCover} from '~Umbraco/Utils/mapHeroVideoCover';
import {IMediaCover} from 'design-system/src/Components/Card/CarouselCard/CarouselCard';
import {IMapButtonResult} from '~Umbraco/Utils/element/mapButton';
import Metrics from 'design-system/src/Theme/Metrics';
import Typography from 'design-system/src/Components/Typography/Typography';
import {IUmbracoRouteParams} from '~Umbraco/Interface/Umbraco.interface';

import AdvantageList from './AdvantageList/AdvantageList';
import ServiceList from './ServiceList/ServiceList';
import PaywallHomePageLayout from './PaywallHomePageLayout';
import PaywallConfirmPage from '../PaywallConfirmPage/PaywallConfirmPage';
import useDefaultPaywall from '@src/Store/selectors/useDefaultPaywall';
import useAppNavigation from '@src/utils/useAppNavigation';
import {AppRoutesEnum} from '@src/Constant/routes';
import useBreakpoint from 'design-system/src/WebComponent/Grid/hooks/useBreakpoint';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import {IPaywallOrientation} from '../PaywallSection.type';
import {UmbTypeChecker} from '@src/Umbraco/Utils/UmbTypeChecker';
import {tracker} from '@src/Services/Amplitude/hooks/tracker';

interface PaywallHomePageProps {
  id?: string;
  onSelectPlan?: () => void;
  variant?: 'paywall' | 'choosePlan';
  orientation?: IPaywallOrientation;
}

interface PaywallData {
  mediaCover?: IMediaCover;
  title?: string;
  subtitle?: string;
  buttons?: IMapButtonResult[];
  mentionLegalLabel?: string;
  mentionLink?: IUmbracoRouteParams;
  renderBlocks?: ReactNode[];
}

type BlockContent = {
  __typename?: string;
  [key: string]: any;
};
type Block = {content?: BlockContent};

const renderAdvantageList = (block: Block, key: number) => (
  <AdvantageList
    key={key}
    avantages={block.content?.advantages || []}
    template={block.content?.template as any}
    style={{marginBottom: Metrics.verticalScale(16)}}
  />
);

const renderServiceList = (block: Block, key: number) => (
  <ServiceList
    key={key}
    services={block.content?.services || []}
    title={block.content?.title}
    style={{
      paddingTop: Metrics.verticalScale(27),
      paddingBottom: Metrics.verticalScale(24),
      marginBottom: Metrics.verticalScale(16),
    }}
  />
);

const renderSubscriptionsList = (
  block: Block,
  key: number,
  orientation: IPaywallOrientation,
  isMobile: boolean,
  isChoosePlan: boolean,
  navigation: any,
  t: any,
) => {
  const blockContent = block.content || {};
  const listProduct =
    (blockContent.subscriptionsList
      ?.map((_el: BlockContent) =>
        UmbTypeChecker.asType(_el, 'Subscription') && _el.productID
          ? _el.productID
          : null,
      )
      .filter(Boolean) as string[]) || [];

  return (
    <View key={key} style={{}}>
      <PaywallConfirmPage
        variant="paywall"
        orientation={orientation}
        listFilter={i => listProduct.includes(i.planId)}
        productIdOrder={listProduct}
        needStore={true}
        horizontal={!isMobile}
        contentWrapperStyle={
          isMobile
            ? undefined
            : {
                paddingHorizontal: 0,
              }
        }
      />
      <View
        style={{
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: isMobile ? Metrics.verticalScale(19) : 86 - 16,
          marginBottom: isChoosePlan
            ? 0
            : isMobile
              ? Metrics.verticalScale(68)
              : Metrics.verticalScale(68),
          paddingHorizontal: isMobile ? 74 : Metrics.horizontalScale(60),
        }}>
        <Pressable
          style={{marginTop: Metrics.verticalScale(7)}}
          onPress={() => {
            navigation.navigate(AppRoutesEnum.TERMS_AND_CONDITION, {
              state: {params: {type: 'termsConditions'}},
            });
          }}>
          <Typography
            children={t('Terms and conditions')}
            variant="ui3"
            color="gray"
          />
        </Pressable>
      </View>
    </View>
  );
};

const shouldRenderPaywallBlock = (
  block: Block,
  isChoosePlan: boolean,
): boolean =>
  !isChoosePlan || UmbTypeChecker.isType(block?.content, 'SubscriptionsList');

const PaywallHomePage: FC<PaywallHomePageProps> = ({
  id,
  variant = 'paywall',
  orientation = 'portrait',
}) => {
  const navigation = useAppNavigation();
  const {t} = useTranslation('profile');
  const defaultPaywall = useDefaultPaywall();
  const {data} = usePaywalByIdQuery({
    variables: {id: id},
    skip: !id,
    errorPolicy: 'all',
  });
  const {isMobile} = useBreakpoint();
  const isChoosePlan = variant === 'choosePlan';
  const isLandscape = orientation === 'landscape';

  const paywallResponse: IPaywallFragmentFragment | undefined =
    data?.paywall ?? defaultPaywall;

  const blockRenderers: Record<
    string,
    (block: any, key: number) => React.ReactNode
  > = {
    AdvantagesList: renderAdvantageList,
    ServicesList: renderServiceList,
    SubscriptionsList: (block, key) =>
      renderSubscriptionsList(
        block,
        key,
        orientation,
        isMobile,
        isChoosePlan,
        navigation,
        t,
      ),
  };

  const paywallData: PaywallData = useMemo(() => {
    if (!paywallResponse) return {};
    const {cover, title, subtitle, pageBlocks} = paywallResponse;

    const firstCoverContent = cover?.[0]?.content;
    const mediaCover =
      UmbTypeChecker.isType(firstCoverContent, 'HeroVideoCover') &&
      firstCoverContent
        ? mapHeroVideoCover(firstCoverContent, isMobile)
        : undefined;

    const renderBlocks =
      pageBlocks
        ?.filter(
          block => block && shouldRenderPaywallBlock(block, isChoosePlan),
        )
        .map((block, blockIndex) => {
          if (!block?.content) return null;
          const renderer = blockRenderers[block.content.__typename ?? ''];
          return renderer ? renderer(block, blockIndex) : null;
        })
        .filter(Boolean) ?? [];

    return {
      mediaCover,
      title,
      subtitle,
      renderBlocks,
    };
  }, [paywallResponse, isMobile, isChoosePlan, orientation, navigation, t]);

  return (
    paywallData && (
      <PaywallHomePageLayout
        mediaCover={paywallData.mediaCover}
        title={paywallData.title}
        subtitle={paywallData.subtitle}
        renderBlocks={paywallData.renderBlocks}
        orientation={orientation}
        isMobile={isMobile}
        isLandscape={isLandscape}
      />
    )
  );
};

export default PaywallHomePage;
